import {Select as SelectBase, SelectOptionItem} from '@yandex-delivery/blocks'
import React, {useCallback} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import {scoped} from '../../utils'
import {SelectProps} from './Select.types'

export function Select<T extends unknown>({
  name,
  'data-testid': dataTestId,
  helperText,
  options,
  rules,
  defaultValue,
  shouldUnregister,
  onChange: onSelect,
  ...otherProps
}: SelectProps<T>): React.ReactElement<SelectProps<T>> {
  const {control} = useFormContext()

  const {
    fieldState: {error},
    field: {onBlur, onChange, value},
  } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  })

  const handleChange = useCallback(
    (item: SelectOptionItem<T>[] | SelectOptionItem<T> | null) => {
      const value = item ? getValue<T>(item) : undefined

      onSelect && onSelect(item)
      onChange(value)
      onBlur()
    },
    [onBlur, onChange, onSelect],
  )

  return (
    <SelectBase
      data-testid={dataTestId}
      error={Boolean(error?.message)}
      helperText={error?.message ?? helperText}
      name={name}
      onBlur={onBlur}
      onChange={handleChange}
      options={options}
      value={value}
      {...otherProps}
    />
  )
}

function getValue<T>(item: SelectOptionItem<T> | SelectOptionItem<T>[]) {
  return Array.isArray(item) ? item.map(i => i.value) : item.value
}

export const ScopedSelect = scoped(Select)
